import api from '../api';
import {
  DefaultWellTestResponse,
  DefaultWellTestApiData,
  DefaultWellTestData,
  DefaultWellTestPayload,
  DefaultWellTestFormData,
} from '../../types/wellTestTypes';

const transformDefaultWellTestData = (apiData: DefaultWellTestApiData): DefaultWellTestData => {
  return {
    TestDate: apiData.testDate,
    Duration: apiData.duration,
    OilRate: apiData.oilRate,
    WaterRate: apiData.waterRate,
    GasRate: apiData.gasRate,
    TubingPressure: apiData.tubingPressure,
    CasingPressure: apiData.casingPressure,
    Runtime: apiData.runtime,
    StrokeLength: apiData.strokeLength,
    FluidAbovePump: apiData.fluidAbovePump,
    Pumpsize: apiData.pumpsize,
    SPM: apiData.spm,
    FlowlineTemp: apiData.flowlineTemp,
    CasingTemp: apiData.casingTemp,
    WaterSG: apiData.waterSG,
    OilAPIGravity: apiData.oilAPIGravity,
    Approved: apiData.approved,
    CO2: apiData.cO2,
    HighPressureGasRate: apiData.highPressureGasRate,
    CasingGasRate: apiData.casingGasRate,
    GasLiftGasRate: apiData.gasLiftGasRate,
    FlowlinePressure: apiData.flowlinePressure,
    TubingTemp: apiData.tubingTemp,
    PumpIntakePressure: apiData.pumpIntakePressure,
    PumpDischargePressure: apiData.pumpDischargePressure,
    PumpIntakeTemp: apiData.pumpIntakeTemp,
    MotorTemp: apiData.motorTemp,
    DriveCurrent: apiData.driveCurrent,
    DriveSpeed: apiData.driveSpeed,
    RodRPM: apiData.rodRPM,
    RodTorque: apiData.rodTorque,
    TriplexDischargePressure: apiData.triplexDischargePressure,
    SuctionPressure: apiData.suctionPressure,
    LinePressureToSetting: apiData.linePressureToSetting,
  };
};

// Transform form data to API format
const transformDefaultWellTestFormDataToApiFormat = (formData: DefaultWellTestFormData): DefaultWellTestPayload => {
  const transformedData: DefaultWellTestPayload = {
    testDate: formData.testDate,
    duration: formData.duration ? Number(formData.duration) : null,
    oilRate: formData.oilRate ? Number(formData.oilRate) : null,
    waterRate: formData.waterRate ? Number(formData.waterRate) : null,
    gasRate: formData.gasRate ? Number(formData.gasRate) : null,
    tubingPressure: formData.tubingPressure ? Number(formData.tubingPressure) : null,
    casingPressure: formData.casingPressure ? Number(formData.casingPressure) : null,
    runtime: formData.runtime ? Number(formData.runtime) : null,
    strokeLength: formData.strokeLength ? Number(formData.strokeLength) : null,
    fluidAbovePump: formData.fluidAbovePump ? Number(formData.fluidAbovePump) : null,
    pumpsize: formData.pumpsize ? Number(formData.pumpsize) : null,
    spm: formData.spm ? Number(formData.spm) : null,
    flowlineTemp: formData.flowlineTemp ? Number(formData.flowlineTemp) : null,
    casingTemp: formData.casingTemp ? Number(formData.casingTemp) : null,
    waterSG: formData.waterSG ? Number(formData.waterSG) : null,
    oilAPIGravity: formData.oilAPIGravity ? Number(formData.oilAPIGravity) : null,
    cO2: formData.co2 ? Number(formData.co2) : null,
    highPressureGasRate: formData.highPressureGasRate ? Number(formData.highPressureGasRate) : null,
    casingGasRate: formData.casingGasRate ? Number(formData.casingGasRate) : null,
    gasLiftGasRate: formData.gasLiftGasRate ? Number(formData.gasLiftGasRate) : null,
    flowlinePressure: formData.flowlinePressure ? Number(formData.flowlinePressure) : null,
    tubingTemp: formData.tubingTemp ? Number(formData.tubingTemp) : null,
    pumpIntakePressure: formData.pumpIntakePressure ? Number(formData.pumpIntakePressure) : null,
    pumpDischargePressure: formData.pumpDischargePressure ? Number(formData.pumpDischargePressure) : null,
    pumpIntakeTemp: formData.pumpIntakeTemp ? Number(formData.pumpIntakeTemp) : null,
    motorTemp: formData.motorTemp ? Number(formData.motorTemp) : null,
    driveCurrent: formData.driveCurrent ? Number(formData.driveCurrent) : null,
    driveSpeed: formData.driveSpeed ? Number(formData.driveSpeed) : null,
    rodRPM: formData.rodRPM ? Number(formData.rodRPM) : null,
    rodTorque: formData.rodTorque ? Number(formData.rodTorque) : null,
    triplexDischargePressure: formData.triplexDischargePressure ? Number(formData.triplexDischargePressure) : null,
    suctionPressure: formData.suctionPressure ? Number(formData.suctionPressure) : null,
    linePressureToSetting: formData.linePressureToSetting ? Number(formData.linePressureToSetting) : null,
  };

  // Remove fields with empty or null values except for testDate
  return Object.fromEntries(
    Object.entries(transformedData).filter(([key, value]) => key === 'testDate' || (value !== '' && value !== null)),
  ) as DefaultWellTestPayload;
};

export const fetchDefaultWellTestData = async (
  assetId: string,
  signal?: AbortSignal,
): Promise<{ values: DefaultWellTestData[] } | null> => {
  try {
    const response = await api.get(`/WellTests/${assetId}`, {
      headers: {
        'x-api-version': '2.0',
      },
      signal,
    });
    const apiResponse: DefaultWellTestResponse = response.data;

    if (apiResponse && apiResponse.values) {
      const transformedData = apiResponse.values.map(transformDefaultWellTestData);
      return { values: transformedData };
    }

    return null;
  } catch (error) {
    console.error('Error fetching Default well test data:', error);
    return null;
  }
};

export const createDefaultWellTest = async (assetId: string, data: DefaultWellTestPayload): Promise<string> => {
  try {
    const response = await api.post(`/WellTests/${assetId}`, data, {
      headers: {
        'x-api-version': '2.0',
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error creating Default well test:', error);
    throw error;
  }
};

export const updateDefaultWellTest = async (assetId: string, data: DefaultWellTestPayload): Promise<string> => {
  try {
    const response = await api.put(`/WellTests/${assetId}`, data, {
      headers: {
        'x-api-version': '2.0',
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error updating Default well test:', error);
    throw error;
  }
};

export const saveDefaultWellTest = async (
  formData: DefaultWellTestFormData,
  assetId: string,
  isEdit: boolean,
): Promise<string> => {
  const transformedData = transformDefaultWellTestFormDataToApiFormat(formData);

  if (isEdit) {
    return await updateDefaultWellTest(assetId, transformedData);
  } else {
    return await createDefaultWellTest(assetId, transformedData);
  }
};

export { transformDefaultWellTestFormDataToApiFormat };
